import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu, Phone, X, ShoppingBag, UserRound } from "lucide-react";
import { LOCATIONS, MORE_LINKS, NAV_LINKS } from "../lib/content";
import UtilityBar from "./UtilityBar";
import { useTrip } from "../hooks/useTrip";
import { useAuth } from "../hooks/useAuth";

/** "MORE" dropdown. Opens on click, closes on Escape, on blur-out and on
 *  navigation, so it is fully usable from the keyboard. */
const MoreMenu = () => {
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    const onClick = (e) => {
      if (wrap.current && !wrap.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  return (
    <div ref={wrap} className="relative">
      <button
        type="button"
        data-testid="nav-more-toggle"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md px-2.5 py-2 text-[13px] font-semibold text-white/85 transition-colors hover:bg-white/10 hover:text-white"
      >
        More
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      {open && (
        <div
          data-testid="nav-more-menu"
          className="absolute right-0 top-full mt-2 w-60 overflow-hidden rounded-2xl border border-white/10 bg-navy shadow-2xl shadow-navy/40"
        >
          {MORE_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-more-link-${l.to.replace(/\//g, "-").replace(/^-/, "")}`}
              className={({ isActive }) =>
                `block px-4 py-3 text-[13px] font-semibold transition-colors ${
                  isActive ? "text-ember" : "text-white/85 hover:bg-white/10 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
};

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const { count } = useTrip();
  const { signedIn } = useAuth();

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-navy/95 backdrop-blur-md shadow-lg shadow-navy/20" : "bg-navy/80 backdrop-blur-sm"
      }`}
    >
      <UtilityBar />
      <div className="mx-auto max-w-[88rem] px-4 lg:px-8">
        {/* Fixed height at every width: the row never grows as content changes. */}
        <div className="flex h-16 lg:h-20 items-center justify-between gap-3">
          <Link
            to="/"
            data-testid="header-logo-link"
            className="flex items-center shrink-0 text-white"
            aria-label="West Branch Marina home"
          >
            {/* Genuine vector wordmark. Height-constrained with w-auto so the
                226:62 ratio is never stretched. */}
            <img
              src="/site-assets/brand/westbranch-marina-logo.svg"
              alt="West Branch Marina, Lake Milton Marina"
              width={226}
              height={62}
              data-testid="header-logo-image"
              className="h-9 w-auto lg:h-11"
            />
          </Link>

          <nav aria-label="Main navigation" className="hidden xl:flex items-center gap-0.5 shrink-0">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.to.replace(/\//g, "-").replace(/^-/, "")}`}
                className={({ isActive }) =>
                  `whitespace-nowrap shrink-0 px-2.5 py-2 text-[13px] font-semibold rounded-md transition-colors ${
                    isActive ? "text-ember" : "text-white/85 hover:text-white hover:bg-white/10"
                  }`
                }
              >
                {l.shortLabel || l.label}
              </NavLink>
            ))}
            <MoreMenu />
          </nav>

          <div className="flex items-center gap-1.5 shrink-0">
            <Link
              to="/cart"
              data-testid="header-cart-link"
              aria-label={count > 0 ? `Your trip, ${count} item${count === 1 ? "" : "s"}` : "Your trip, empty"}
              className="relative inline-flex h-10 w-10 items-center justify-center rounded-full text-white transition-colors hover:bg-white/10"
            >
              <ShoppingBag className="h-5 w-5" aria-hidden="true" />
              {count > 0 && (
                <span
                  data-testid="header-cart-badge"
                  className="absolute -right-0.5 -top-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-ember px-1 text-[11px] font-bold text-white"
                >
                  {count}
                </span>
              )}
            </Link>
            <Link
              to="/boat-rentals/west-branch"
              data-testid="header-book-now-button"
              className="hidden sm:inline-flex shrink-0 whitespace-nowrap items-center rounded-full bg-ember px-4 py-2.5 text-[13px] font-bold text-white transition-all hover:bg-ember-hover hover:-translate-y-0.5 active:translate-y-0"
            >
              Book Now
            </Link>
            {signedIn ? (
              <Link
                to="/account/trips"
                data-testid="header-account-link"
                className="hidden md:inline-flex shrink-0 whitespace-nowrap items-center gap-2 rounded-full border border-white/25 px-3.5 py-2 text-[13px] font-bold text-white hover:bg-white/10"
              >
                <UserRound className="h-4 w-4 shrink-0" aria-hidden="true" />
                My trips
              </Link>
            ) : (
              <Link
                to="/account/login"
                data-testid="header-signin-link"
                className="hidden md:inline-flex shrink-0 whitespace-nowrap items-center gap-2 px-2.5 py-2 text-[13px] font-semibold text-white/85 hover:text-white"
              >
                <UserRound className="h-4 w-4 shrink-0" aria-hidden="true" />
                Sign in
              </Link>
            )}
            <button
              type="button"
              data-testid="mobile-menu-toggle"
              aria-expanded={open}
              aria-controls="mobile-drawer"
              aria-label={open ? "Close menu" : "Open menu"}
              onClick={() => setOpen((v) => !v)}
              className="xl:hidden inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-white hover:bg-white/10 transition-colors"
            >
              {open ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div
          id="mobile-drawer"
          data-testid="mobile-drawer"
          className="xl:hidden border-t border-white/10 bg-navy max-h-[calc(100vh-4rem)] overflow-y-auto"
        >
          <nav aria-label="Mobile navigation" className="px-5 py-4 flex flex-col">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-link-${l.to.replace(/\//g, "-").replace(/^-/, "")}`}
                className={({ isActive }) =>
                  `py-3 text-base font-semibold border-b border-white/10 ${
                    isActive ? "text-ember" : "text-white/90"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            {MORE_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-link-${l.to.replace(/\//g, "-").replace(/^-/, "")}`}
                className={({ isActive }) =>
                  `py-3 text-base font-semibold border-b border-white/10 ${
                    isActive ? "text-ember" : "text-white/90"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="pt-5 space-y-3">
              <Link
                to={signedIn ? "/account/trips" : "/account/login"}
                data-testid={signedIn ? "mobile-account-link" : "mobile-signin-link"}
                className="flex items-center gap-2 rounded-xl bg-white/10 px-4 py-3 text-sm font-semibold text-white"
              >
                <UserRound className="h-4 w-4" aria-hidden="true" />
                {signedIn ? "My trips" : "Sign in"}
              </Link>
              {Object.values(LOCATIONS).map((loc) => (
                <a
                  key={loc.slug}
                  href={loc.phoneHref}
                  data-testid={`mobile-call-${loc.slug}`}
                  className="flex items-center justify-between rounded-xl bg-white/10 px-4 py-3 text-white"
                >
                  <span className="text-sm font-semibold">{loc.park}</span>
                  <span className="flex items-center gap-2 text-sm font-bold text-ember">
                    <Phone className="h-4 w-4" aria-hidden="true" />
                    {loc.phone}
                  </span>
                </a>
              ))}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
